import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";

// Admin document classification review + bulk correction. Only changes the `kind` label —
// never touches revisions, acknowledgements, assignments or signatures. No title-based auto-guessing.
export default function PolicyClassify() {
  const [docs, setDocs] = useState([]);
  const [kinds, setKinds] = useState({ rams: [], policy: [] });
  const [changes, setChanges] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/policies/classification/preview");
    setDocs(r.data.documents);
    setKinds({ rams: r.data.rams_kinds, policy: r.data.policy_kinds });
    setChanges({});
  };
  useEffect(() => { load(); }, []);

  const set = (id, kind) => setChanges((c) => ({ ...c, [id]: kind }));
  const pending = Object.entries(changes).filter(([id, k]) => k && docs.find((d) => d.id === id)?.kind !== k);

  const apply = async () => {
    if (!pending.length) return;
    setBusy(true);
    try {
      const r = await api.post("/policies/classification/apply", { changes: pending.map(([id, kind]) => ({ id, kind })) });
      toast.success(`Reclassified ${r.data.changed.length} document(s)`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not apply"); }
    finally { setBusy(false); }
  };

  const allKinds = [...kinds.policy, ...kinds.rams];
  const group = (cls) => docs.filter((d) => d.doc_class === cls);

  return (
    <div className="flex flex-col gap-4" data-testid="policy-classify">
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
        Review each document's classification. Changing the type moves it between <b>Policies &amp; Procedures</b> and <b>RAMS</b>. This only relabels the document — signatures, revisions and assignments are untouched. Classification is never guessed from the title.
      </div>
      {["rams", "policy"].map((cls) => {
        const list = group(cls);
        return (
          <div key={cls}>
            <h3 className="font-display font-bold text-slate-800 mb-2">{cls === "rams" ? "RAMS" : "Policies & Procedures"} <span className="text-slate-400 text-sm">· {list.length}</span></h3>
            <div className="flex flex-col gap-1.5">
              {list.length === 0 && <p className="text-sm text-slate-400">None.</p>}
              {list.map((d) => (
                <div key={d.id} data-testid={`classify-row-${d.id}`} className="flex items-center gap-3 rounded-lg border border-slate-100 bg-white p-2.5 text-sm">
                  <span className="font-mono text-[11px] text-slate-400 w-16 shrink-0">{d.ref}</span>
                  <span className="flex-1 truncate text-slate-800">{d.title}</span>
                  <select data-testid={`classify-kind-${d.id}`} value={changes[d.id] ?? d.kind} onChange={(e) => set(d.id, e.target.value)}
                    className="h-8 rounded border border-slate-200 text-xs px-2">
                    {allKinds.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      <div className="sticky bottom-0 bg-white/95 py-2">
        <Button data-testid="classify-apply" disabled={busy || !pending.length} onClick={apply} className="bg-emerald-700 hover:bg-emerald-800">
          {pending.length ? `Apply ${pending.length} change(s)` : "No changes to apply"}
        </Button>
      </div>
    </div>
  );
}
